import { useCallback, useState } from "react";
import { defaultProfile, emailFromIdToken, markOnboardingComplete, NovaSynthProfile, persistProfile, saveProfile } from "@/lib/profile";
import { SlackConnect } from "./SlackConnect";

interface OnboardingWizardProps {
  idToken: string;
  onComplete: (firstCompetitor?: string) => void;
}

const STEPS = [
  { id: 1, label: "Company",     short: "Who you are" },
  { id: 2, label: "Competitors", short: "Add up to 5 key competitors to monitor" },
  { id: 3, label: "Positioning", short: "Sharpen your threat read (optional)" },
  { id: 4, label: "Alerts",      short: "Notifications" },
  { id: 5, label: "Integrations", short: "Connect tools" },
  { id: 6, label: "All Set",     short: "Launch" },
];

const LAST_STEP = STEPS.length; // 6
const LAST_CONTENT_STEP = LAST_STEP - 1; // 5 (Integrations) - last step with nav buttons

const INDUSTRIES = [
  "Technology & Software",
  "Financial Services & Banking",
  "Healthcare & Life Sciences",
  "Retail & eCommerce",
  "Manufacturing & Industrial",
  "Professional Services & Consulting",
  "Education & EdTech",
  "Cybersecurity",
  "Media & Entertainment",
  "Telecommunications",
  "Logistics & Supply Chain",
  "Real Estate & PropTech",
  "Energy & Utilities",
  "Insurance",
  "Legal & LegalTech",
  "Automotive",
  "Travel & Hospitality",
  "Government & Public Sector",
  "Food & Beverage",
  "Non-profit & NGO",
];
const SIZES = ["1-50", "51-200", "201-1,000", "1,000+"];
const ROLES = [
  "CEO / Co-Founder",
  "CTO / VP Engineering",
  "CMO / VP Marketing",
  "VP Sales / Sales Director",
  "VP Product / Product Manager",
  "Chief Strategy Officer",
  "Competitive Intelligence Analyst",
  "Business Development Manager",
  "Sales Representative / Account Executive",
  "Marketing Manager / Demand Gen",
  "Customer Success Manager",
  "Business Analyst / Strategist",
  "Data Analyst",
  "Operations Manager",
  "Investor / VC Analyst",
  "Other",
];

// ---------------------------------------------------------------------------
// Sub-components for each step
// ---------------------------------------------------------------------------

interface SelectProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}

function Select({ value, onChange, options, placeholder, disabled }: SelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3.5 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-sm text-slate-100 focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200 appearance-none disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="" disabled className="text-slate-600">{placeholder}</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0d1424] text-slate-100">{o}</option>)}
      </select>
      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
        <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </div>
    </div>
  );
}

interface ToggleProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
  color?: string;
}

function Toggle({ enabled, onChange, label, description, color = "bg-brand-500" }: ToggleProps) {
  return (
    <label className="flex items-center justify-between gap-4 p-3.5 bg-[#070d1a] border border-[#1a2640] rounded-xl cursor-pointer hover:border-[#263350] transition-colors group">
      <div>
        <p className="text-sm font-medium text-slate-200">{label}</p>
        <p className="text-[11px] text-slate-600 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onChange(!enabled)}
        className={`relative w-10 h-5.5 rounded-full transition-colors duration-200 shrink-0 focus:outline-none focus:ring-2 focus:ring-brand-500/50 cursor-pointer ${enabled ? color : "bg-[#1a2640]"}`}
        style={{ height: "22px" }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
          style={{ transform: enabled ? "translateX(18px)" : "translateX(0)" }}
        />
      </button>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

interface StepProps {
  profile: NovaSynthProfile;
  onChange: (patch: Partial<NovaSynthProfile>) => void;
}

function StepCompany({ profile, onChange }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">Company name <span className="text-red-500">*</span></label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
            </svg>
          </div>
          <input
            type="text"
            value={profile.companyName}
            onChange={(e) => onChange({ companyName: e.target.value })}
            placeholder="Your company name"
            className="w-full pl-10 pr-4 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-slate-100 placeholder-slate-700 text-sm focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200"
          />
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">Industry</label>
        <Select value={profile.industry} onChange={(v) => onChange({ industry: v })} options={INDUSTRIES} placeholder="Select industry…" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">Company size</label>
          <Select value={profile.companySize} onChange={(v) => onChange({ companySize: v })} options={SIZES} placeholder="Employees…" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">Your role</label>
          <Select value={profile.role} onChange={(v) => onChange({ role: v })} options={ROLES} placeholder="Role…" />
        </div>
      </div>
    </div>
  );
}

function StepCompetitors({ profile, onChange, onRunNow }: StepProps & { onRunNow: (firstCompetitor?: string) => void }) {
  const [input, setInput] = useState("");

  function add() {
    const trimmed = input.trim();
    if (!trimmed || profile.competitors.includes(trimmed) || profile.competitors.length >= 5) return;
    onChange({ competitors: [...profile.competitors, trimmed] });
    setInput("");
  }

  function remove(c: string) {
    onChange({ competitors: profile.competitors.filter((x) => x !== c) });
  }

  const atMax = profile.competitors.length >= 5;
  const firstCompetitor = profile.competitors[0];
  // A fast value-first exit: once a company + at least one competitor exist, the
  // user can launch their first analysis right here instead of stepping through
  // the rest of setup. The remaining steps stay reachable later via Settings.
  const canRunNow = profile.companyName.trim().length > 0 && Boolean(firstCompetitor);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 leading-relaxed">
          Add up to 5 key competitors to monitor. You can always add more later from settings.
        </p>
        <span className={`shrink-0 ml-3 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
          atMax
            ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
            : "text-brand-400 border-brand-500/30 bg-brand-500/10"
        }`}>
          {profile.competitors.length} / 5
        </span>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
          </div>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="e.g. Salesforce or salesforce.com"
            disabled={atMax}
            className="w-full pl-10 pr-4 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-slate-100 placeholder-slate-700 text-sm focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-mono"
          />
        </div>
        <button
          type="button"
          onClick={add}
          disabled={!input.trim() || atMax}
          className="px-4 py-2.5 bg-brand-700/30 hover:bg-brand-700/50 border border-brand-600/30 text-brand-300 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          Add
        </button>
      </div>

      {atMax && (
        <p className="text-[11px] text-amber-400/80 flex items-center gap-1.5 animate-fade-in">
          <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          Maximum 5 competitors reached
        </p>
      )}

      {profile.competitors.length > 0 ? (
        <div className="space-y-2">
          {profile.competitors.map((c) => (
            <div key={c} className="flex items-center justify-between px-3.5 py-2.5 bg-[#070d1a] border border-[#1a2640] rounded-xl animate-fade-in">
              <div className="flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                <span className="text-sm font-mono text-slate-200">{c}</span>
              </div>
              <button
                type="button"
                onClick={() => remove(c)}
                className="text-slate-600 hover:text-red-400 transition-colors cursor-pointer p-1"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 border border-dashed border-[#1a2640] rounded-xl">
          <svg className="w-8 h-8 text-slate-700 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-slate-600">No competitors added yet</p>
        </div>
      )}

      {/* Value-first fast exit: launch the first analysis straight from here. */}
      {canRunNow && (
        <div className="p-4 bg-[#070d1a] border border-brand-600/25 rounded-xl space-y-2.5">
          <div>
            <p className="text-sm font-semibold text-slate-200">Ready when you are</p>
            <p className="mt-0.5 text-[11px] text-slate-500 leading-relaxed">
              Skip the rest of setup and get your first brief now. You can fine-tune
              alerts and integrations anytime in Settings.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onRunNow(firstCompetitor)}
            className="w-full py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand cursor-pointer"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.97l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
            </svg>
            Analyze {firstCompetitor} now
          </button>
        </div>
      )}
    </div>
  );
}

const POSITIONING_FIELDS: { key: "icp" | "valueProps" | "differentiators" | "targetSegments" | "winLossNotes"; label: string; placeholder: string; rows?: number }[] = [
  { key: "icp", label: "Ideal customer profile", placeholder: "e.g. Mid-market B2B SaaS, 200-2,000 employees, RevOps-led buying" },
  { key: "targetSegments", label: "Target segments", placeholder: "e.g. Financial services, healthcare, retail" },
  { key: "valueProps", label: "Your value propositions", placeholder: "What you lead with - the outcomes you sell", rows: 2 },
  { key: "differentiators", label: "Your differentiators", placeholder: "What competitors can't easily match", rows: 2 },
  { key: "winLossNotes", label: "Win / loss notes", placeholder: "Why you win, why you lose - patterns from recent deals", rows: 2 },
];

function StepPositioning({ profile, onChange }: StepProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-brand-700/10 border border-brand-600/20 rounded-xl">
        <svg className="w-4 h-4 text-brand-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
        <p className="text-xs text-brand-300">Optional - the more context you add, the sharper every threat read and counter-move becomes. You can skip this and add it later in Settings.</p>
      </div>

      {POSITIONING_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{f.label}</label>
          <textarea
            value={profile[f.key] ?? ""}
            onChange={(e) => onChange({ [f.key]: e.target.value } as Partial<NovaSynthProfile>)}
            placeholder={f.placeholder}
            rows={f.rows ?? 1}
            className="w-full px-3.5 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-slate-100 placeholder-slate-700 text-sm focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200 resize-none leading-relaxed"
          />
        </div>
      ))}
    </div>
  );
}

function StepAlerts({ profile, onChange }: StepProps) {
  const FREQUENCIES = [
    { value: "realtime" as const, label: "Real-time",    desc: "Instant on completion" },
    { value: "daily" as const,    label: "Daily digest", desc: "Morning summary" },
    { value: "weekly" as const,   label: "Weekly",       desc: "Friday roundup" },
  ];

  const TRIGGERS = [
    { key: "highThreat" as const,    label: "High threat detected (score ≥ 7)", desc: "Immediate alert when a competitor scores high threat" },
    { key: "newProduct" as const,    label: "New product / feature launched",    desc: "Detected from video, web, and news signals" },
    { key: "pricingChange" as const, label: "Pricing change detected",            desc: "Monitors pricing pages and announcements" },
    { key: "executiveHire" as const, label: "Executive hire or departure",        desc: "Tracks leadership changes at competitors" },
  ];

  return (
    <div className="space-y-5">
      {/* Email alerts - re-enabled now that email delivery is live. */}
      <Toggle
        enabled={profile.emailAlerts}
        onChange={(v) => onChange({ emailAlerts: v })}
        label="Email me alerts"
        description="Get intelligence updates delivered to your inbox."
      />

      {/* Email address input - defaults to the account's signup email. */}
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
          Alert Email Address
        </label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <input
            type="email"
            value={profile.alertEmail}
            onChange={(e) => onChange({ alertEmail: e.target.value })}
            placeholder="your@email.com"
            disabled={!profile.emailAlerts}
            className="w-full pl-10 pr-4 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-slate-100 placeholder-slate-700 text-sm focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          />
        </div>
        <p className="mt-1.5 text-[11px] text-slate-600">We've pre-filled your account email - change it anytime.</p>
      </div>

      {/* Notification frequency */}
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-3">Notification Frequency</label>
        <div className="grid grid-cols-3 gap-2">
          {FREQUENCIES.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => onChange({ notificationFrequency: f.value })}
              className={[
                "p-3 rounded-xl border text-left transition-all duration-200 cursor-pointer",
                profile.notificationFrequency === f.value
                  ? "border-cyan-500 bg-cyan-500/10 shadow-glow-brand-sm"
                  : "border-slate-600 bg-slate-800/50 hover:border-[#263350]",
              ].join(" ")}
            >
              <p className={`text-xs font-semibold ${profile.notificationFrequency === f.value ? "text-cyan-400" : "text-slate-100"}`}>{f.label}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{f.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Alert triggers - toggles */}
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-3">Alert Triggers</label>
        <div className="space-y-2">
          {TRIGGERS.map((t) => (
            <Toggle
              key={t.key}
              enabled={profile.alertTriggers[t.key]}
              onChange={(v) => onChange({ alertTriggers: { ...profile.alertTriggers, [t.key]: v } })}
              label={t.label}
              description={t.desc}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StepIntegrations({ profile, onChange, idToken }: StepProps & { idToken: string }) {
  return (
    <div className="space-y-4">
      {/* Guided Slack connect. During onboarding we keep the webhook in the local
          draft (and persist best-effort to the account if signed in) so the test
          button can verify it server-side. */}
      <SlackConnect
        idToken={idToken}
        value={profile.slackWebhook}
        compact
        onSave={async (webhook) => {
          onChange({ slackWebhook: webhook });
          if (idToken) {
            await persistProfile(idToken, { ...profile, slackWebhook: webhook });
          }
        }}
      />

      <p className="text-[11px] text-slate-600 text-center">
        Integrations are optional - you can connect or change this later in Settings.
      </p>
    </div>
  );
}

function StepReady({ profile, onLaunch }: { profile: NovaSynthProfile; onLaunch: (c?: string) => void }) {
  const [selected, setSelected] = useState(profile.competitors[0] ?? "");

  return (
    <div className="space-y-5">
      {/* Success icon */}
      <div className="flex justify-center">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full bg-emerald-500/10 animate-ping" style={{ animationDuration: "2s" }} />
          <div className="relative w-20 h-20 rounded-full bg-emerald-950/40 border border-emerald-600/30 flex items-center justify-center shadow-glow-green">
            <svg className="w-10 h-10 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="text-center">
        <h3 className="text-lg font-bold text-white mb-1">
          NovaSynth is ready{profile.companyName ? `, ${profile.companyName}` : ""}!
        </h3>
        <p className="text-sm text-slate-500">Your intelligence platform is configured and ready to deploy.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "Specialists", value: "4 active" },
          { label: "Competitors", value: profile.competitors.length > 0 ? `${profile.competitors.length} tracked` : "None added" },
          { label: "Depth",       value: profile.depth === "deep" ? "Deep (8-12 min)" : "Quick (3-5 min)" },
          { label: "Notifications", value: profile.notificationFrequency === "realtime" ? "Real-time" : profile.notificationFrequency === "daily" ? "Daily" : "Weekly" },
        ].map((s) => (
          <div key={s.label} className="px-3.5 py-3 bg-[#070d1a] border border-[#1a2640] rounded-xl">
            <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-widest">{s.label}</p>
            <p className="text-sm font-semibold text-slate-200 mt-0.5">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Launch first analysis */}
      {profile.competitors.length > 0 && (
        <div className="p-4 bg-[#070d1a] border border-brand-600/20 rounded-xl space-y-3">
          <p className="text-xs font-semibold text-slate-400">Launch first analysis</p>
          <Select
            value={selected}
            onChange={setSelected}
            options={profile.competitors}
            placeholder="Pick a competitor…"
          />
          <button
            type="button"
            onClick={() => onLaunch(selected || undefined)}
            disabled={!selected}
            className="w-full py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.97l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
            </svg>
            Run analysis for {selected || "…"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------

export function OnboardingWizard({ idToken, onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState<"forward" | "back">("forward");
  // Default the alert email to the signed-in account's email so the user never
  // has to re-type it. Email alerts stay OFF by default (opt-in).
  const [profile, setProfile] = useState<NovaSynthProfile>(() => {
    const base = defaultProfile();
    const signupEmail = emailFromIdToken(idToken);
    if (signupEmail) base.alertEmail = signupEmail;
    return base;
  });

  const patch = useCallback((p: Partial<NovaSynthProfile>) => {
    setProfile((prev) => ({ ...prev, ...p }));
  }, []);

  function go(target: number) {
    setDir(target > step ? "forward" : "back");
    setStep(target);
  }

  function next() {
    if (step < LAST_STEP) go(step + 1);
  }

  function back() {
    if (step > 1) go(step - 1);
  }

  function finish(firstCompetitor?: string) {
    saveProfile(profile);
    markOnboardingComplete();
    onComplete(firstCompetitor);
  }

  function skip() {
    // From the last content step (Integrations), "Skip" advances to All Set;
    // from earlier steps it finishes setup straight away.
    if (step === LAST_CONTENT_STEP) { go(LAST_STEP); return; }
    finish();
  }

  const canNext = step !== 1 || profile.companyName.trim().length > 0;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden"
      style={{
        backgroundColor: "#020617",
        backgroundImage: `linear-gradient(rgba(26,38,64,0.42) 1px, transparent 1px), linear-gradient(90deg, rgba(26,38,64,0.42) 1px, transparent 1px)`,
        backgroundSize: "28px 28px",
      }}
    >
      {/* Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[600px] h-[600px] rounded-full opacity-15 animate-orb"
          style={{ background: "radial-gradient(circle, rgba(74,86,232,0.3) 0%, transparent 70%)", top: "-200px", left: "-150px" }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-10 animate-orb-slow"
          style={{ background: "radial-gradient(circle, rgba(34,211,238,0.25) 0%, transparent 70%)", bottom: "-100px", right: "-80px" }} />
      </div>

      <div className="relative w-full max-w-lg animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-700/20 border border-brand-600/25 mb-3 relative">
            <div className="absolute inset-0 rounded-xl bg-brand-500/10 blur-sm" />
            <svg className="relative w-5 h-5 text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-white tracking-tight">Set up NovaSynth</h1>
          <p className="mt-1 text-xs text-slate-600">Configure your competitive intelligence platform</p>
        </div>

        {/* Step progress */}
        <div className="flex items-center gap-0 mb-6 bg-[#0d1424] border border-[#1a2640] rounded-2xl p-1.5 overflow-x-auto">
          {STEPS.map((s, i) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className="flex items-center flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => done && go(s.id)}
                  disabled={!done}
                  className={[
                    "flex-1 flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-200 min-w-0",
                    active ? "bg-brand-700/20 border border-brand-600/25" : "",
                    done ? "cursor-pointer hover:bg-[#1a2640]" : "cursor-default",
                  ].join(" ")}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-brand-600 text-white" : "bg-[#1a2640] text-slate-600"
                  }`}>
                    {done ? (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : s.id}
                  </div>
                  <span className={`text-[9px] font-medium truncate w-full text-center ${active ? "text-brand-300" : done ? "text-emerald-500" : "text-slate-700"}`}>
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`h-px w-3 shrink-0 transition-colors ${done ? "bg-emerald-700/50" : "bg-[#1a2640]"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="relative p-px rounded-2xl bg-gradient-to-br from-brand-700/35 via-[#1a2640] to-[#22d3ee]/15">
          <div className="bg-[#0d1424] rounded-2xl shadow-card-lg overflow-hidden">
            <div className="px-6 pt-5 pb-2 border-b border-[#1a2640] flex items-center gap-2.5">
              <span className="text-[10px] font-bold text-brand-500 uppercase tracking-widest">Step {step} of {LAST_STEP}</span>
              <span className="w-1 h-1 rounded-full bg-[#1a2640]" />
              <h2 className="text-sm font-semibold text-slate-200">
                {STEPS[step - 1].label} <span className="text-slate-600 font-normal"> - {STEPS[step - 1].short}</span>
              </h2>
            </div>

            <div
              key={step}
              className={`px-6 py-5 ${dir === "forward" ? "animate-slide-up" : "animate-fade-in"}`}
              style={{ minHeight: "280px" }}
            >
              {step === 1 && <StepCompany profile={profile} onChange={patch} />}
              {step === 2 && <StepCompetitors profile={profile} onChange={patch} onRunNow={finish} />}
              {step === 3 && <StepPositioning profile={profile} onChange={patch} />}
              {step === 4 && <StepAlerts profile={profile} onChange={patch} />}
              {step === 5 && <StepIntegrations profile={profile} onChange={patch} idToken={idToken} />}
              {step === LAST_STEP && <StepReady profile={profile} onLaunch={finish} />}
            </div>

            {/* Navigation */}
            {step < LAST_STEP && (
              <div className="px-6 py-4 border-t border-[#1a2640] bg-[#091020]/40 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={back}
                      className="px-4 py-2 text-sm text-slate-500 hover:text-slate-300 hover:bg-[#1a2640] rounded-xl transition-all duration-200 flex items-center gap-1.5 cursor-pointer"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                      </svg>
                      Back
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {(step === 3 || step === LAST_CONTENT_STEP) && (
                    <button
                      type="button"
                      onClick={skip}
                      className="px-4 py-2 text-xs text-slate-600 hover:text-slate-400 transition-colors cursor-pointer"
                    >
                      Skip for now
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={next}
                    disabled={!canNext}
                    className="px-5 py-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-1.5 shadow-glow-brand-sm hover:shadow-glow-brand disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {step === LAST_CONTENT_STEP ? "Finish setup" : "Continue"}
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Final step: skip to dashboard */}
            {step === LAST_STEP && (
              <div className="px-6 py-4 border-t border-[#1a2640] bg-[#091020]/40">
                <button
                  type="button"
                  onClick={() => finish()}
                  className="w-full py-2.5 text-xs text-slate-500 hover:text-slate-300 border border-[#1a2640] hover:border-[#263350] rounded-xl transition-all duration-200 cursor-pointer"
                >
                  Skip to dashboard →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-0.5 bg-[#1a2640] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-600 to-[#22d3ee] rounded-full transition-all duration-500"
            style={{ width: `${((step - 1) / (LAST_STEP - 1)) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
